<template>
  <AppContainer as="main">
    <section class="w-full py-24">
      <div class="flex flex-col items-center justify-center text-center gap-10">
        <h1 class="max-w-4xl text-5xl md:text-7xl font-semibold text-white">
          Sell Smarter. <br />Keep Customers Longer.
        </h1>
        <p class="max-w-3xl text-lg md:text-xl font-light text-grey-dark-300">
          LayUp helps retailers boost sales, reduce drop-offs, and build customer loyalty with flexible, interest-free
          payment solutions that work online and in-store.
        </p>
        <Button variant="default" size="lg" class="w-full sm:w-auto py-6 my-4" @click="scrollTo('#enquiry-form')"
          >Get in Touch</Button
        >
        <AnimationComponent>
          <img :src="layupEnquiryBanner" alt="LayUp Banner" class="w-full h-full object-cover" />
        </AnimationComponent>
      </div>
    </section>
    <Separator />
    <section class="w-full py-24">
      <AnimationComponent>
        <div class="flex flex-col items-center justify-center text-center gap-10 mb-12">
          <h2 class="max-w-3xl text-4xl md:text-5xl font-semibold text-white">
            Tired of abandoned carts and missed sales?
          </h2>
          <p class="max-w-3xl text-lg md:text-xl font-light text-grey-dark-300">
            Your customers aren’t walking away because they don’t want what you’re selling, they’re walking away because
            they can’t afford to pay for it upfront.
          </p>
          <p class="max-w-3xl text-lg md:text-xl font-light text-grey-dark-300">LayUp changes that.</p>
          <p class="max-w-3xl text-lg md:text-xl font-light text-grey-dark-300">
            We help small and mid-sized merchants remove affordability barriers and convert browsers into loyal,
            returning buyers.
          </p>
        </div>
      </AnimationComponent>
      <AnimationComponent>
        <div class="flex flex-col md:flex-row items-center justify-center gap-10 md:items-stretch">
          <Card class="w-full md:w-auto rounded-3xl">
            <CardContent class="flex flex-col py-7 px-10 h-full">
              <h3 class="text-xl font-semibold">Your Challenges</h3>
              <Separator class="mt-4 mb-12" />
              <ul class="text-lg text-grey-dark-300">
                <li class="flex items-center mb-10">
                  <CircleX class="w-6 h-6 mr-4 shrink-0" />
                  Low online conversions
                </li>
                <li class="flex items-center mb-10">
                  <CircleX class="w-6 h-6 mr-4 shrink-0" />
                  High cart abandonment
                </li>
                <li class="flex items-center mb-10">
                  <CircleX class="w-6 h-6 mr-4 shrink-0" />
                  Limited payment options
                </li>
                <li class="flex items-center">
                  <CircleX class="w-6 h-6 mr-4 shrink-0" />
                  Low repeat sales
                </li>
              </ul>
            </CardContent>
          </Card>
          <Card class="border-success-500/30 rounded-3xl">
            <CardContent class="flex flex-col py-7 px-10 h-full">
              <h3 class="text-xl font-semibold">Our Solution</h3>
              <Separator class="mt-4 mb-12 bg-success-500/30" />
              <ul class="text-lg text-success-500">
                <li class="flex items-center mb-10">
                  <CircleCheck class="w-6 h-6 mr-4 shrink-0" />
                  <p>Let customers pay over time — no credit, no risk.</p>
                </li>
                <li class="flex items-center mb-10">
                  <CircleCheck class="w-6 h-6 mr-4 shrink-0" />
                  <p>Offer flexible instalments right at checkout.</p>
                </li>
                <li class="flex items-center mb-10">
                  <CircleCheck class="w-6 h-6 mr-4 shrink-0" />
                  <p>Integrate LayUp's payment tools seamlessly with Shopify, WooCommerce, Magento or POS.</p>
                </li>
                <li class="flex items-center">
                  <CircleCheck class="w-6 h-6 mr-4 shrink-0" />
                  <p>Reward plan completions automatically with BOOST digital vouchers to drive repeat purchases</p>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </AnimationComponent>
    </section>
    <Separator />
    <section class="w-full py-24 overflow-clip">
      <p class="text-center mb-12 text-grey-dark-300">Trusted by leading merchants</p>
      <MerchantLogoCarousel variant="light" :hideText="true" />
    </section>
    <Separator />
    <section class="w-full pt-24">
      <AnimationComponent>
        <img :src="layupEnquiryPhones" alt="LayUp Enquiry Phones" class="w-full h-full object-cover" />
      </AnimationComponent>
    </section>
    <section class="w-full py-24" id="enquiry-form">
      <AnimationComponent>
        <div class="flex flex-col items-center justify-center text-center gap-10 mb-12">
          <h2 class="max-w-3xl text-4xl md:text-5xl font-semibold text-white">Let’s grow your business, together.</h2>
          <p class="max-w-3xl text-lg md:text-xl font-light text-grey-dark-300">
            Whether you’re looking to offer flexible lay-by payments or reward loyalty with BOOST, we’ll help you choose
            the right fit for your store.
          </p>
          <div class="w-full max-w-3xl bg-brand-900 py-8 px-6 md:py-16 md:px-14 rounded-3xl">
            <EnquiryForm />
          </div>
        </div>
      </AnimationComponent>
    </section>
    <Separator />
    <FeaturesSection
      :features="features"
      title="Why leading retailers choose LayUp"
      description="Merchants choose LayUp because it empowers shoppers to buy confidently while helping stores grow sustainably."
      :hasButton="false"
      :add-padding-x="false"
    />
    <AnimationComponent>
      <div class="flex flex-col items-center justify-center text-center gap-10 mb-12">
        <p class="max-w-3xl text-lg md:text-xl font-light text-grey-dark-300">
          With LayUp, you’re not just adding a payment method – you’re building a smarter sales engine.
        </p>
        <Button @click="scrollTo('#enquiry-form')" variant="default" size="lg" class="w-full sm:w-auto py-6 my-4">
          Get in Touch
        </Button>
      </div>
    </AnimationComponent>
    <Separator />
    <section class="w-full py-24">
      <AnimationComponent>
        <div class="flex flex-col items-center justify-center text-center gap-6">
          <p class="max-w-3xl text-2xl md:text-3xl font-light text-success-400 italic">
            “LayUp has helped us convert customers who would have walked away. It’s simple, secure, and keeps our sales
            cycle moving.”
          </p>
          <p class="max-w-3xl text-lg md:text-xl font-light text-grey-dark-300">— LayUp Merchant Partner</p>
        </div>
      </AnimationComponent>
    </section>
    <Separator />
    <section class="w-full py-24">
      <AnimationComponent>
        <div class="flex flex-col items-center justify-center text-center gap-10 mb-12">
          <h2 class="max-w-3xl text-4xl md:text-5xl font-semibold text-white">
            Ready to grow your business with LayUp?
          </h2>
          <p class="max-w-3xl text-lg md:text-xl font-light text-grey-dark-300">
            Join hundreds of South African retailers who sell smarter – with zero credit risk and happier, debt-free
            customers.
          </p>
        </div>
      </AnimationComponent>
      <AnimationComponent>
        <div class="flex flex-col items-center justify-center text-center gap-10">
          <img :src="growYourBusinessImage" alt="Grow Your Business with LayUp" class="w-full h-full object-cover" />
          <Button variant="default" size="lg" class="w-full sm:w-auto py-6 my-4" @click="scrollTo('#enquiry-form')"
            >Enquire Now</Button
          >
        </div>
      </AnimationComponent>
    </section>
  </AppContainer>
</template>

<script setup lang="ts">
import growYourBusinessImage from '@/assets/images/business/grow-your-business-with-layup.webp';
import layupEnquiryBanner from '@/assets/images/business/layup-merchant-enquiry-banner.webp';
import layupEnquiryPhones from '@/assets/images/business/layup-merchant-enquiry-phones.webp';
import AnimationComponent from '@/components/AnimationComponent.vue';
import AppContainer from '@/components/AppContainer.vue';
import EnquiryForm from '@/components/business/EnquiryForm.vue';
import FeaturesSection from '@/components/business/FeaturesSection.vue';
import MerchantLogoCarousel from '@/components/MerchantLogoCarousel.vue';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Separator from '@/components/ui/separator/SeparatorComponent.vue';
import { CircleCheck, CircleX } from 'lucide-vue-next';

const scrollTo = (selector: string) => {
  const element = document.querySelector(selector);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

const features = [
  {
    icon: 'ChartLine',
    title: 'Boost sales',
    description: 'by up to 30% with flexible, upfront deposit options.',
  },
  {
    icon: 'HandCoins',
    title: 'Zero credit risk',
    description: 'customers pay before collecting their goods.',
  },
  {
    icon: 'CreditCard',
    title: 'Predictable payments',
    description: 'No chargebacks or bad debt.',
  },
  {
    icon: 'Link',
    title: 'Seamless integration',
    description: 'with Shopify, WooCommerce, and in-store POS.',
  },
  {
    icon: 'BadgeCheck',
    title: 'Trusted nationwide',
    description: 'by top brands across retail, tech, travel, decor, and lifestyle.',
  },
];
</script>
